import { useQuery } from '@tanstack/react-query';
import {
  PLANON_DEFAULT_COUNTRY_SYSCODE,
  type PlanonReferenceKind,
  type PlanonReferenceEntry,
} from '@hcc/types';
import { api } from '../../../lib/api';

/** Cached Planon reference options for a kind (dropdown source). */
export function usePlanonReference(kind: PlanonReferenceKind) {
  return useQuery({
    queryKey: ['planon-reference', kind],
    queryFn: () =>
      api<{ entries: PlanonReferenceEntry[]; fetched_at: string | null }>(
        `/api/admin/planon/reference?kind=${kind}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export const FIELD_LABEL_CLS = 'text-xs font-medium text-fg-muted';
export const FIELD_INPUT_CLS =
  'mt-1 w-full rounded-token-sm border border-line/20 bg-surface px-2 py-1.5 text-sm';

/**
 * The editable Planon fields. Identity (name/code/email/start date) is
 * injected server-side from our person record, so it is shown read-only and
 * not part of the draft. Keys match the API selections schema 1:1.
 */
export interface PlanonDraft {
  genderRef: number | null;
  propertyRef: number | null;
  departmentRef: number | null;
  costCentreRef: number | null;
  employmentTypeRef: number | null;
  tariffGroupRef: number | null;
  personPositionRef: number | null;
  country2: number | null;
  tradeSyscode: number | null;
  managerSyscode: number | null;
  division: string | null;
  employeeCategory: string | null;
  workerType: string | null;
  workerCategory: string | null;
  reportingTeam: string | null;
  departmentAccount: string | null;
  costType: string | null;
  primaryTradeRole: string | null;
  personTypes: number[];
  primaryZone: string | null;
  secondaryZone: string | null;
  mobilePhone: string | null;
}

export function emptyDraft(): PlanonDraft {
  return {
    genderRef: null,
    propertyRef: null,
    departmentRef: null,
    costCentreRef: null,
    employmentTypeRef: null,
    tariffGroupRef: null,
    personPositionRef: null,
    country2: PLANON_DEFAULT_COUNTRY_SYSCODE,
    tradeSyscode: null,
    managerSyscode: null,
    division: null,
    employeeCategory: null,
    workerType: null,
    workerCategory: null,
    reportingTeam: null,
    departmentAccount: null,
    costType: null,
    primaryTradeRole: null,
    personTypes: [],
    primaryZone: null,
    secondaryZone: null,
    mobilePhone: null,
  };
}

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v)
    ? v
    : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))
      ? Number(v)
      : null;
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

/**
 * Prefill a draft from a copied UsrEmployeeWelcome record. Position is not on
 * that record (it comes from the roster entry), so it is passed separately.
 * PersonType, mobile and country are not copied.
 */
export function draftFromCopy(
  record: Record<string, unknown>,
  positionSyscode: number | null,
): PlanonDraft {
  return {
    genderRef: num(record.GenderRef),
    propertyRef: num(record.PropertyRef),
    departmentRef: num(record.DepartmentRef),
    costCentreRef: num(record.CostCentreRef),
    employmentTypeRef: num(record.EmploymenttypeRef),
    tariffGroupRef: num(record.WorkingHoursTariffGroupRef),
    personPositionRef: positionSyscode,
    country2: PLANON_DEFAULT_COUNTRY_SYSCODE,
    tradeSyscode: num(record.FreeInteger1),
    managerSyscode: num(record.FreeString32),
    division: str(record.FreeString1),
    employeeCategory: str(record.FreeString2),
    workerType: str(record.FreeString7),
    workerCategory: str(record.FreeString8),
    reportingTeam: str(record.FreeString11),
    departmentAccount: str(record.FreeString12),
    costType: str(record.FreeString16),
    primaryTradeRole: str(record.FreeString18),
    personTypes: [],
    primaryZone: str(record.FreeString9),
    secondaryZone: str(record.FreeString21),
    mobilePhone: null,
  };
}

/** Draft -> API selections, omitting empties (the zod schema wants absent, not null). */
export function toSelections(d: PlanonDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(d)) {
    if (key === 'personTypes') {
      if ((value as number[]).length > 0) out.personTypes = value;
      continue;
    }
    if (value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}
